/**
 * Highlights as ProseMirror inline decorations.
 *
 * Replaces the old string-slicing span renderer and the "prune highlights on
 * blur" hack. Source of truth stays useHighlightStore (in-memory overlay;
 * canonical persistence is yaqatw's XML parts):
 *
 *  - store → editor: syncHighlightDecorations() rebuilds the DecorationSet
 *    from the store via positionMap (dispatched as a meta-only transaction).
 *  - edits: the DecorationSet is mapped through every transaction, so
 *    highlights follow text edits automatically.
 *  - editor → store: syncHighlightsFromEditor() translates each mapped
 *    decoration back to {paragraphId, startOffset, endOffset} and updates the
 *    store (string-typed offsets, per the wire format); highlights whose text
 *    was deleted are removed.
 */

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, EditorState } from "@tiptap/pm/state";
import { Decoration, DecorationSet, EditorView } from "@tiptap/pm/view";
import { Highlight } from "../types";
import { useHighlightStore } from "../store/highlightStore";
import { paraRangeToPmRange, pmRangeToParaRange } from "./positionMap";
import { now } from "../utils/idGenerator";
import { joinCodeNames } from "../utils/codeNames";
import { getSettingValue } from "../settings/settingsStore";

export const highlightPluginKey = new PluginKey<DecorationSet>("qdaHighlights");

/** Settings > Highlights > Default highlight color, for highlights/codes with no color of their own. */
export function getDefaultHighlightColor(): string {
  const v = getSettingValue("highlights", "defaultColor");
  return typeof v === "string" && v ? v : "#ffff00";
}

/** Settings > Highlights > Highlight opacity, shared by the editor and PDF-tab renderers. */
export function getHighlightOpacity(): number {
  const v = getSettingValue("highlights", "opacity");
  return typeof v === "number" && v > 0 ? v : 0.4;
}

/**
 * Document-wide visibility toggle, mirroring WordDocumentHost's
 * `contentControl.appearance` (boundingBox/hidden) bulk flip — hostBridge's
 * hideAllHighlights/refreshHighlightsInDocument handlers set this and force a
 * decoration rebuild (see syncHighlightDecorations). qda has no per-control
 * appearance, so it's a single flag consulted here rather than per-highlight
 * state.
 */
let highlightsHidden = false;

export function setHighlightsHidden(hidden: boolean): void {
  highlightsHidden = hidden;
}

/**
 * Number of times each code's color is repeated as a gradient stop, versus a
 * single "white" stop between neighbors. With CSS's default even spacing of
 * unpositioned stops, a higher repeat count gives each color a proportionally
 * wider plateau and squeezes the white buffer between them thinner — without
 * ever computing a percentage ourselves.
 */
const COLOR_STOP_REPEAT = 4;

/**
 * Smoothly blended color1 → white → color2 → white → color3 ... for N codes.
 * White is a single, unpositioned stop between each pair of colors — never
 * placed at the same position as a color, so CSS always interpolates a
 * gradual fade rather than a hard cut. It reads as a thin "buffer" (not a
 * blended third color) purely because each color's own stops are repeated
 * (COLOR_STOP_REPEAT) to claim most of the width, leaving white a
 * proportionally narrow gap — no percentage math needed. Degrades to a flat
 * background-color for a single code (current look) — this is the
 * browser-only improvement real Word can't do (OOXML's w:highlight is a
 * single fixed-swatch enum, one color per highlight).
 */
export function highlightBackgroundStyle(colors: string[]): string {
  const list = colors.length ? colors : [getDefaultHighlightColor()];
  if (list.length === 1) return `background-color: ${list[0]};`;
  const stops: string[] = [];
  list.forEach((color, i) => {
    for (let r = 0; r < COLOR_STOP_REPEAT; r++) stops.push(color);
    if (i < list.length - 1) stops.push("white");
  });
  return `background-image: linear-gradient(to right, ${stops.join(", ")}); background-color: transparent;`;
}

function buildDecorations(state: EditorState, highlights: Highlight[]): DecorationSet {
  if (highlightsHidden) return DecorationSet.create(state.doc, []);
  const decos: Decoration[] = [];
  for (const h of highlights) {
    if (h.attrs.isDeleted === "true") continue;
    const range = paraRangeToPmRange(
      state,
      h.attrs.paragraphId,
      parseInt(h.attrs.startOffset, 10),
      parseInt(h.attrs.endOffset, 10)
    );
    if (!range || range.from >= range.to) continue;
    const title = joinCodeNames(h.codes) || h.attrs.text;
    const colors =
      h.codes && h.codes.length > 0
        ? h.codes.map((c) => c.color || getDefaultHighlightColor())
        : [h.attrs.color || getDefaultHighlightColor()];
    decos.push(
      Decoration.inline(
        range.from,
        range.to,
        {
          "data-highlight-id": h.id,
          title,
          style:
            `${highlightBackgroundStyle(colors)} opacity: ${getHighlightOpacity()}; ` +
            `box-decoration-break: clone; -webkit-box-decoration-break: clone;`,
        },
        { highlightId: h.id }
      )
    );
  }
  return DecorationSet.create(state.doc, decos);
}

export const HighlightDecorations = Extension.create({
  name: "qdaHighlights",

  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: highlightPluginKey,
        state: {
          init: (_config, state) =>
            buildDecorations(state, useHighlightStore.getState().highlights),
          apply(tr, set, _oldState, newState) {
            const highlights = tr.getMeta(highlightPluginKey) as Highlight[] | undefined;
            if (highlights) return buildDecorations(newState, highlights);
            return tr.docChanged ? set.map(tr.mapping, tr.doc) : set;
          },
        },
        props: {
          decorations(state) {
            return highlightPluginKey.getState(state);
          },
        },
      }),
    ];
  },
});

/** Rebuild decorations from the given highlight list (store → editor). */
export function syncHighlightDecorations(view: EditorView, highlights: Highlight[]) {
  view.dispatch(view.state.tr.setMeta(highlightPluginKey, highlights));
}

/**
 * After a doc-changing transaction, push the mapped decoration ranges back
 * into the highlight store so the plugin's offset-based model stays in sync
 * (the non-destructive replacement for the old prune-on-blur step).
 */
export function syncHighlightsFromEditor(view: EditorView) {
  const set = highlightPluginKey.getState(view.state);
  if (!set) return;
  const store = useHighlightStore.getState();

  for (const h of store.highlights) {
    if (h.attrs.isDeleted === "true") continue;
    const deco = set.find(undefined, undefined, (spec) => spec.highlightId === h.id)[0];
    if (!deco) {
      // The highlighted text was deleted entirely; drop the overlay.
      store.removeHighlight(h.id);
      continue;
    }
    const range = pmRangeToParaRange(view.state, deco.from, deco.to);
    if (!range || range.startOffset === range.endOffset) {
      store.removeHighlight(h.id);
      continue;
    }
    const text = view.state.doc.textBetween(deco.from, deco.to);
    if (
      range.paragraphId !== h.attrs.paragraphId ||
      String(range.startOffset) !== h.attrs.startOffset ||
      String(range.endOffset) !== h.attrs.endOffset ||
      text !== h.attrs.text
    ) {
      store.updateHighlight({
        ...h,
        modified: now(),
        attrs: {
          ...h.attrs,
          paragraphId: range.paragraphId,
          startOffset: String(range.startOffset),
          endOffset: String(range.endOffset),
          text,
        },
      });
    }
  }
}
